// utils/fileUploadUtils.js
export const uploadFileToServer = async (file, token, onProgress) => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const xhr = new XMLHttpRequest();
    
    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve({
            url: response.url,
            originalFileName: file.name,
            contentType: file.type,
            fileSizeBytes: file.size,
            uploadedAt: Date.now(),
            success: true
          });
        } catch (error) {
          reject(new Error('Invalid response from server'));
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was cancelled'));
    });
    
    xhr.open('POST', '/api/upload-file'); // Your upload endpoint
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
};

// Alternative version using fetch (if you prefer)
export const uploadFileToServerFetch = async (file, token, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await fetch('/api/upload-file', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    return {
      url: result.url,
      originalFileName: file.name,
      contentType: file.type,
      fileSizeBytes: file.size,
      uploadedAt: Date.now(),
      success: true
    };
  } catch (error) {
    throw new Error(`Upload failed: ${error.message}`);
  }
};

// Utility to cancel an ongoing upload (for XMLHttpRequest version)
export const createCancellableUpload = (file, token, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const xhr = new XMLHttpRequest();
  
  const uploadPromise = new Promise((resolve, reject) => {
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const percentComplete = (event.loaded / event.total) * 100;
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve({
            url: response.url,
            originalFileName: file.name,
            contentType: file.type,
            fileSizeBytes: file.size,
            uploadedAt: Date.now(),
            success: true
          });
        } catch (error) {
          reject(new Error('Invalid response from server'));
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was cancelled'));
    });
    
    xhr.open('POST', '/api/upload-file');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(formData);
  });
  
  return {
    promise: uploadPromise,
    cancel: () => xhr.abort()
  };
};

// Retry upload with exponential backoff
export const uploadFileWithRetry = async (file, token, onProgress, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await uploadFileToServer(file, token, onProgress);
    } catch (error) {
      lastError = error;
      
      if (attempt < maxRetries) {
        // Exponential backoff: wait 1s, 2s, 4s between retries
        const delay = Math.pow(2, attempt - 1) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        console.log(`Upload attempt ${attempt} failed, retrying in ${delay}ms...`);
      }
    }
  }
  
  throw lastError;
};

// Batch upload multiple files
export const uploadMultipleFiles = async (files, token, onProgress, onFileComplete) => {
  const results = [];
  const errors = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      const result = await uploadFileToServer(
        file, 
        token, 
        (progress) => {
          if (onProgress) {
            onProgress({
              fileIndex: i,
              fileName: file.name,
              progress,
              overall: ((i / files.length) * 100) + (progress / files.length)
            });
          }
        }
      );
      
      results.push(result);
      
      if (onFileComplete) {
        onFileComplete(i, result, null);
      }
      
    } catch (error) {
      errors.push({ fileIndex: i, fileName: file.name, error });
      
      if (onFileComplete) {
        onFileComplete(i, null, error);
      }
    }
  }
  
  return {
    results,
    errors,
    success: errors.length === 0
  };
};